﻿import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  usePharmacyDrugs,
  useDeletePharmacyDrug,
  useBulkDeletePharmacyDrugs,
  usePharmacyDrugCategories,
  usePharmacyGenericNames,
  usePharmacyCompanies,
  usePharmacyStorageRacks,
  usePharmacyBatches,
  useAdjustPharmacyStock,
  useIssuePharmacyStock,
} from '../../services/httpServices/queries';
import type { PharmacyDrug } from '../../services/httpServices/pharmacyDrugService';
import { PharmacyLayout } from './PharmacyLayout';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { DrugDetailsModal } from '../../components/ui/DrugDetailsModal';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  X,
  Loader2,
  AlertCircle,
  Pencil,
  Trash2,
  Package,
  Tags,
  FlaskConical,
  Building2,
  LayoutGrid,
  CheckCircle,
  XCircle,
  Filter,
  SlidersHorizontal,
  ArrowUpDown,
  AlertTriangle,
} from 'lucide-react';

type StockModalMode = 'adjust' | 'issue' | null;

export function PharmacyDrugs() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const limit = 20;

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [categoryId, setCategoryId] = useState(() => params.get('categoryId') || '');
  const [genericNameId, setGenericNameId] = useState(() => params.get('genericNameId') || '');
  const [companyId, setCompanyId] = useState(() => params.get('companyId') || '');
  const [storageRackId, setStorageRackId] = useState(() => params.get('storageRackId') || '');
  const [lowOnly, setLowOnly] = useState(() => params.get('lowOnly') === 'true');

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      categoryId: categoryId || undefined,
      genericNameId: genericNameId || undefined,
      companyId: companyId || undefined,
      storageRackId: storageRackId || undefined,
      lowOnly: lowOnly || undefined,
    }),
    [debouncedSearch, categoryId, genericNameId, companyId, storageRackId, lowOnly],
  );

  const { data, isLoading, error } = usePharmacyDrugs(page, limit, filters);

  const { data: categoriesData } = usePharmacyDrugCategories(1, 1000);
  const { data: genericsData } = usePharmacyGenericNames(1, 1000);
  const { data: companiesData } = usePharmacyCompanies(1, 1000);
  const { data: racksData } = usePharmacyStorageRacks();
  const deleteDrug = useDeletePharmacyDrug();
  const bulkDeleteDrug = useBulkDeletePharmacyDrugs();

  const drugs = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;
  const lowCount = Number(data?.stats?.lowStockCount ?? 0);
  const categories = categoriesData?.data || [];
  const generics = genericsData?.data || [];
  const companies = companiesData?.data || [];
  const racks = racksData?.data || [];

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    if (categoryId) newParams.set('categoryId', categoryId);
    if (genericNameId) newParams.set('genericNameId', genericNameId);
    if (companyId) newParams.set('companyId', companyId);
    if (storageRackId) newParams.set('storageRackId', storageRackId);
    if (lowOnly) newParams.set('lowOnly', 'true');
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, categoryId, genericNameId, companyId, storageRackId, lowOnly, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search, categoryId, genericNameId, companyId, storageRackId, lowOnly]);

  const clearFilters = () => {
    setPage(1);
    setSearch('');
    setCategoryId('');
    setGenericNameId('');
    setCompanyId('');
    setStorageRackId('');
    setLowOnly(false);
  };

  const hasFilters = search || categoryId || genericNameId || companyId || storageRackId || lowOnly;

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(drugs.map((drug: any) => drug.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [drugs]);

  const allSelected = drugs.length > 0 && drugs.every((drug: any) => selectedIds.has(drug.id));
  const someSelected = drugs.some((drug: any) => selectedIds.has(drug.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = drugs.length > 0 && drugs.every((drug: any) => prev.has(drug.id));
      return everySelected ? new Set() : new Set(drugs.map((drug: any) => drug.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Delete confirmation popup (single row action + bulk action)
  const [confirmState, setConfirmState] = useState<{
    mode: 'single' | 'bulk';
    drugs: any[];
  } | null>(null);

  const requestDelete = (drug: any) => setConfirmState({ mode: 'single', drugs: [drug] });

  const requestBulkDelete = () => {
    const selected = drugs.filter((drug: any) => selectedIds.has(drug.id));
    if (selected.length === 0) return;
    setConfirmState({ mode: 'bulk', drugs: selected });
  };

  const handleConfirmDelete = async () => {
    if (!confirmState) return;
    const { mode, drugs: targets } = confirmState;
    try {
      if (mode === 'single') {
        await deleteDrug.mutateAsync(targets[0].id);
      } else {
        await bulkDeleteDrug.mutateAsync(targets.map((drug: any) => drug.id));
        setSelectedIds(new Set());
        toast.success(`Deleted ${targets.length} drug${targets.length === 1 ? '' : 's'}`);
      }
    } catch {
      if (mode === 'bulk') toast.error('Failed to delete some drugs');
    } finally {
      setConfirmState(null);
    }
  };

  const confirmDrugs = confirmState?.drugs || [];
  const confirmIsBulk = confirmState?.mode === 'bulk';

  // Stock adjustment / FEFO issue modal
  const [stockMode, setStockMode] = useState<StockModalMode>(null);
  const [stockDrug, setStockDrug] = useState<PharmacyDrug | null>(null);

  const openStockModal = (mode: Exclude<StockModalMode, null>, drug: PharmacyDrug) => {
    setStockDrug(drug);
    setStockMode(mode);
  };

  const closeStockModal = () => {
    setStockMode(null);
    setStockDrug(null);
  };

  // Drug details modal (opened by clicking the drug name)
  const [detailsDrug, setDetailsDrug] = useState<PharmacyDrug | null>(null);

  const openDetailsModal = (drug: PharmacyDrug) => setDetailsDrug(drug);
  const closeDetailsModal = () => setDetailsDrug(null);

  return (
    <PharmacyLayout
      title="Items & Stock"
      description="Manage the pharmacy catalog and live stock levels derived from the ledger"
      action={
        <button
          onClick={() => navigate('/pharmacy/drugs/new')}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
        >
          <Plus size={16} />
          Add Drug
        </button>
      }
    >
      {lowCount > 0 && (
        <div className="bg-warning/10 border border-warning/20 rounded-xl p-3 flex items-center gap-2 text-warning text-sm">
          <AlertTriangle size={16} />
          {lowCount} medicine{lowCount === 1 ? '' : 's'} at or below reorder level.
        </div>
      )}

      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-text font-medium">
            <Filter size={16} className="text-primary" />
            <span>Filters</span>
          </div>
          <button
            type="button"
            onClick={() => setLowOnly((v) => !v)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors flex items-center gap-1.5 ${
              lowOnly ? 'bg-warning/10 border-warning/30 text-warning' : 'border-border text-text-muted hover:text-text'
            }`}
          >
            <AlertTriangle size={14} /> Low Stock Only
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search drugs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          <SearchableSelect
            value={categoryId}
            onChange={setCategoryId}
            options={categories.map((c: any) => ({ value: c.id, label: c.name }))}
            placeholder="All Categories"
          />
          <SearchableSelect
            value={genericNameId}
            onChange={setGenericNameId}
            options={generics.map((g: any) => ({ value: g.id, label: g.name }))}
            placeholder="All Generic Names"
          />
          <SearchableSelect
            value={companyId}
            onChange={setCompanyId}
            options={companies.map((c: any) => ({ value: c.id, label: c.name }))}
            placeholder="All Companies"
          />
          <SearchableSelect
            value={storageRackId}
            onChange={setStorageRackId}
            options={racks.map((r: any) => ({ value: r.id, label: r.name }))}
            placeholder="All Storage Racks"
          />
        </div>
        {hasFilters && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-muted">{meta?.total ?? drugs.length} result(s) found</span>
            <button
              onClick={clearFilters}
              className="text-sm text-danger hover:text-danger/80 flex items-center gap-1"
            >
              <X size={14} />
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={requestBulkDelete}
              disabled={bulkDeleteDrug.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteDrug.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteDrug.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                <th className="px-4 py-3 w-10 text-left">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={drugs.length === 0}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                    title="Select all"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code / Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Generic</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Company</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Form / Strength</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Rack</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">In Stock</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Stock Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Active</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={12} className="px-4 py-8">
                    <div className="flex items-center justify-center gap-2 text-danger">
                      <AlertCircle size={18} />
                      <span>Failed to load drugs</span>
                    </div>
                  </td>
                </tr>
              ) : drugs.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center text-text-muted">
                    {hasFilters ? (
                      <span>
                        No drugs match your filters.{' '}
                        <button onClick={clearFilters} className="text-primary hover:underline">
                          Clear filters
                        </button>
                      </span>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Package className="w-10 h-10 text-text-muted/30" />
                        <span>No drugs found. Add your first drug to get started.</span>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                drugs.map((drug: PharmacyDrug) => {
                  const currentStock = Number(drug.currentStock ?? 0);
                  const isLowStock = drug.isLowStock ?? currentStock <= Number(drug.reorderLevel ?? 0);
                  return (
                    <tr key={drug.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(drug.id)}
                          onChange={() => toggleOne(drug.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openDetailsModal(drug)}
                              className="font-medium text-text hover:text-primary hover:underline transition-colors text-left"
                              title="View details"
                            >
                              {drug.name}
                            </button>
                            {drug.itemType === 'ACCESSORY' && (
                              <span className="text-[0.625rem] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                                Accessory
                              </span>
                            )}
                          </div>
                          <p className="text-xs font-mono text-text-muted">{drug.drugCode}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-text">{drug.genericName?.name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background border border-border text-xs">
                          <Tags size={10} />
                          {drug.category?.name || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background border border-border text-xs">
                          <Building2 size={10} />
                          {drug.company?.name || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-text-muted">
                        <div className="flex items-center gap-1">
                          <FlaskConical size={12} />
                          {drug.form}
                          {drug.strength && <span>· {drug.strength}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {drug.storageRack ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background border border-border text-xs">
                            <LayoutGrid size={10} />
                            {drug.storageRack.name}
                          </span>
                        ) : (
                          <span className="text-text-muted/60">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-semibold text-text">
                          {currentStock}{' '}
                          <span className="text-xs font-normal text-text-muted">{drug.unitOfMeasure}</span>
                        </div>
                        <div className="text-[0.6875rem] text-text-muted">Reorder: {drug.reorderLevel}</div>
                      </td>
                      <td className="px-4 py-3 text-text">৳{Number(drug.price).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        {isLowStock ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning">
                            Low
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                            OK
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            drug.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                          }`}
                        >
                          {drug.isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                          {drug.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openStockModal('adjust', drug)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title="Adjust stock"
                          >
                            <SlidersHorizontal size={16} />
                          </button>
                          <button
                            onClick={() => openStockModal('issue', drug)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-warning transition-colors"
                            title="Issue stock (FEFO)"
                          >
                            <ArrowUpDown size={16} />
                          </button>
                          <button
                            onClick={() => navigate(`/pharmacy/drugs/${drug.id}/edit`)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => requestDelete(drug)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={limit}
          onPageChange={setPage}
          itemName="drugs"
        />
      </div>

      {/* Delete confirmation popup */}
      <ConfirmDialog
        open={confirmState !== null}
        title={confirmIsBulk ? 'Delete selected drugs' : 'Delete drug'}
        description={
          confirmIsBulk ? (
            `Delete ${confirmDrugs.length} selected drug${confirmDrugs.length === 1 ? '' : 's'}? This cannot be undone.`
          ) : (
            <>
              Delete <strong>{confirmDrugs[0]?.name}</strong>? This cannot be undone.
            </>
          )
        }
        loading={deleteDrug.isPending || bulkDeleteDrug.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmState(null)}
      />

      {stockMode && stockDrug && (
        <StockModal mode={stockMode} drug={stockDrug} onClose={closeStockModal} />
      )}

      {detailsDrug && (
        <DrugDetailsModal drugId={detailsDrug.id} fallback={detailsDrug} onClose={closeDetailsModal} />
      )}
    </PharmacyLayout>
  );
}

function StockModal({
  mode,
  drug,
  onClose,
}: {
  mode: 'adjust' | 'issue';
  drug: PharmacyDrug;
  onClose: () => void;
}) {
  const { data: batchesData } = usePharmacyBatches(drug.id);
  const batches = batchesData?.data || [];
  const adjustMutation = useAdjustPharmacyStock();
  const issueMutation = useIssuePharmacyStock();

  const [batchId, setBatchId] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState('');

  const isSubmitting = adjustMutation.isPending || issueMutation.isPending;
  const inputClass =
    'w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quantity) return;
    try {
      if (mode === 'adjust') {
        await adjustMutation.mutateAsync({
          drugId: drug.id,
          batchId: batchId || undefined,
          quantity: Number(quantity),
          reason: reason || undefined,
        });
        toast.success('Stock adjusted');
      } else {
        await issueMutation.mutateAsync({ drugId: drug.id, quantity: Number(quantity), notes: reason || undefined });
        toast.success('Stock issued');
      }
      onClose();
    } catch {
      // handled globally
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Package size={18} className="text-primary" />
            <h2 className="text-lg font-semibold text-text">
              {mode === 'adjust' ? 'Adjust Stock' : 'Issue Stock (FEFO)'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-background text-text-muted">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-4">
          <div className="text-sm">
            <div className="font-medium text-text">{drug.name}</div>
            <div className="text-text-muted">
              Current stock: {Number(drug.currentStock ?? 0)} {drug.unitOfMeasure}
            </div>
          </div>

          {mode === 'adjust' && (
            <div>
              <label className="block text-sm font-medium text-text mb-1">Batch (optional)</label>
              <select value={batchId} onChange={(e) => setBatchId(e.target.value)} className={inputClass}>
                <option value="">No specific batch</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.batchNumber} — {Number(b.quantityRemaining)} left
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-text mb-1">
              Quantity {mode === 'adjust' && <span className="text-text-muted">(negative to reduce)</span>}
            </label>
            <input
              type="number"
              required
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-1">Reason / Notes</label>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-text-muted hover:text-text">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              {mode === 'adjust' ? 'Save Adjustment' : 'Issue Stock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
